import React from 'react';
import { MetricCard, MetricCardProps } from '../MetricCard/MetricCard';
import { DataTable, DataTableProps } from '../DataTable/DataTable';
import { Chart, ChartProps } from '../Chart/Chart';
import { StatusCard, StatusCardProps } from '../StatusCard/StatusCard';
import { ProgressCard, ProgressCardProps } from '../ProgressCard/ProgressCard';
import CategoryCard, { CategoryCardProps } from '../CategoryCard/CategoryCard';
import { DashboardActionMenu } from './DashboardActionMenu';
import { cn } from '../../../lib/utils';
import type { WidgetConfig } from '../Widget/Widget';

export type WidgetComponentType =
  | 'MetricCard'
  | 'DataTable'
  | 'Chart'
  | 'StatusCard'
  | 'ProgressCard'
  | 'CategoryCard';

interface WidgetRendererProps {
  componentType: WidgetComponentType;
  props: Record<string, unknown>;
  className?: string;
  layoutClassName?: string;
  // Chat options
  showChat?: boolean;
  componentName?: string;
  componentData?: Record<string, unknown>;
  chatPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  config?: WidgetConfig;
  // Streaming flag for LLM tool use chunks
  isStreaming?: boolean;
  // dashboardId is now provided by context, no longer needed as a prop
}

export const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  componentType,
  props,
  className = 'h-full',
  layoutClassName,
  showChat = false,
  componentName,
  componentData,
  chatPosition = 'top-right',
  config,
  isStreaming = false,
}) => {
  const finalClassName = cn(layoutClassName || className, 'h-full');

  // Determine context type based on component type
  const getContextType = (type: WidgetComponentType) => {
    switch (type) {
      case 'MetricCard':
        return 'metric';
      case 'DataTable':
        return 'table';
      case 'Chart':
        return 'chart';
      default:
        return 'widget';
    }
  };

  const renderWidget = () => {
    switch (componentType) {
      case 'MetricCard':
        return (
          <MetricCard
            {...(props as unknown as MetricCardProps)}
            className={finalClassName}
            isStreaming={isStreaming}
          />
        );

      case 'DataTable':
        return (
          <DataTable
            {...(props as unknown as DataTableProps)}
            className={finalClassName}
            isStreaming={isStreaming}
          />
        );

      case 'Chart':
        return (
          <Chart
            {...(props as unknown as ChartProps)}
            isStreaming={isStreaming}
          />
        );

      case 'StatusCard':
        return (
          <StatusCard
            {...(props as unknown as StatusCardProps)}
            className={finalClassName}
            isStreaming={isStreaming}
          />
        );

      case 'ProgressCard':
        return (
          <ProgressCard
            {...(props as unknown as ProgressCardProps)}
            className={finalClassName}
            isStreaming={isStreaming}
          />
        );

      case 'CategoryCard':
        return (
          <CategoryCard
            {...(props as unknown as CategoryCardProps)}
            className={finalClassName}
            isStreaming={isStreaming}
          />
        );

      default:
        console.warn(`Unknown component type: ${componentType}`);
        return null;
    }
  };

  if (!showChat) {
    return renderWidget();
  }

  return (
    <div className={cn(layoutClassName || className, 'group/widget relative')}>
      {renderWidget()}

      <DashboardActionMenu
        componentName={componentName || componentType}
        componentData={componentData || props}
        contextType={getContextType(componentType)}
        position={chatPosition}
        config={config}
      />
    </div>
  );
};
